import { inject, Service } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';

type ToastVariant = 'success' | 'error';

const TOAST_DURATION_MS = 5500;
const TOAST_STACK_GAP_PX = 12;

const TOAST_ICONS: Record<ToastVariant, string> = {
  success: 'assets/icons/toast-success-check.svg',
  error: 'assets/icons/toast-error-x.svg'
};

@Service()
export class ToastService {
  private readonly toastController = inject(ToastController);
  private readonly liveToasts: HTMLIonToastElement[] = [];

  async showSuccessAsync(message: string): Promise<void> {
    await this.showAsync(message, 'success');
  }

  async showErrorAsync(message: string): Promise<void> {
    await this.showAsync(message, 'error');
  }

  private async showAsync(message: string, variant: ToastVariant): Promise<void> {
    let created: HTMLIonToastElement | undefined;

    try {
      const toast = await this.toastController.create({
        message,
        position: 'top',
        duration: TOAST_DURATION_MS,
        icon: TOAST_ICONS[variant],
        cssClass: ['mbp-toast', `mbp-toast--${variant}`],
        htmlAttributes: { 'data-testid': `${variant}-toast` }
      });

      created = toast;
      const release = () => this.releaseToast(toast);

      this.liveToasts.push(toast);
      void toast.onDidDismiss().then(release, release);

      this.restack();
      await toast.present();
      this.restack();
    } catch (error) {
      if (created) {
        this.releaseToast(created);
      }
      console.error('[ToastService] Failed to present toast:', error);
    }
  }

  private releaseToast(toast: HTMLIonToastElement): void {
    const index = this.liveToasts.indexOf(toast);
    if (index === -1) {
      return;
    }

    this.liveToasts.splice(index, 1);
    this.restack();
  }

  private restack(): void {
    let offset = 0;

    for (const toast of this.liveToasts) {
      toast.style.setProperty('--mbp-toast-stack-offset', `${offset}px`);
      offset += this.measureHeight(toast) + TOAST_STACK_GAP_PX;
    }
  }

  private measureHeight(toast: HTMLIonToastElement): number {
    return toast.shadowRoot?.querySelector('[part~="wrapper"]')?.getBoundingClientRect().height ?? 0;
  }
}
